import React, { useState, useEffect } from "react";
import {
    Button, FormControl, MenuItem, TableRow, Table, TableHead, TableBody, TableCell,
    TableContainer, Paper, Card, Box, Grid, Select, Typography, IconButton,
    Collapse, InputAdornment, Tooltip, Chip, Avatar, TextField,
} from "@material-ui/core";
import ActivityService from "./Locality/Service/activityService";
import StudentService from "./Locality/Service/studentService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SchoolIcon from '@material-ui/icons/School';
import PersonIcon from '@material-ui/icons/Person';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import EventIcon from '@material-ui/icons/Event';

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    pageTitle: {
        fontWeight: 700,
        fontSize: '1.9rem',
        color: '#0f2d4d',
        lineHeight: 1.2,
    },
    pageSubtitle: {
        color: '#6b7280',
        fontSize: '0.9rem',
        marginTop: 2,
    },
    noteBanner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: 12,
        backgroundColor: '#eef4ff',
        border: '1px solid #d6e4ff',
        marginBottom: theme.spacing(2),
    },
    noteIconWrap: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: '#1769d6',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    filtersCard: {
        borderRadius: 14,
        border: '1px solid #e6eaf0',
        boxShadow: '0 2px 10px rgba(16,42,67,0.06)',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    filtersTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    filtersChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 14px',
        borderRadius: 8,
        backgroundColor: '#f4f6fa',
        border: '1px solid #e6eaf0',
        cursor: 'pointer',
        fontWeight: 600,
        color: '#0f2d4d',
        fontSize: '0.9rem',
    },
    countBadge: {
        backgroundColor: '#1769d6',
        color: '#fff',
        borderRadius: 10,
        fontSize: '0.72rem',
        fontWeight: 700,
        minWidth: 18,
        height: 18,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 5px',
    },
    fieldLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#46566b',
        marginBottom: 6,
    },
    primaryBtn: {
        backgroundColor: '#1769d6',
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '8px 18px',
        boxShadow: '0 4px 10px rgba(23,105,214,0.25)',
        '&:hover': { backgroundColor: '#125bbd' },
    },
    resetBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        color: '#46566b',
        border: '1px solid #e6eaf0',
    },
    tableCard: {
        borderRadius: 14,
        border: '1px solid #e6eaf0',
        boxShadow: '0 2px 10px rgba(16,42,67,0.06)',
        overflow: 'hidden',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderBottom: '1px solid #eef1f5',
    },
    iconBtnOutlined: {
        border: '1px solid #e6eaf0',
        borderRadius: 8,
        padding: 7,
    },
    headCell: {
        backgroundColor: '#f4f6fa',
        color: '#46566b',
        fontWeight: 700,
        fontSize: '0.8rem',
        borderBottom: '1px solid #e6eaf0',
        whiteSpace: 'nowrap',
    },
    classChip: {
        backgroundColor: '#eef4ff',
        color: '#1769d6',
        fontWeight: 600,
        height: 24,
    },
    sectionChip: {
        backgroundColor: '#f3efff',
        color: '#7c4dff',
        fontWeight: 600,
        height: 24,
    },
    keyChip: {
        backgroundColor: '#fff8e1',
        color: '#f57f17',
        fontWeight: 600,
        height: 24,
    },
    studentAvatar: {
        width: 28,
        height: 28,
        fontSize: '0.72rem',
        fontWeight: 700,
        backgroundColor: '#1769d6',
        color: '#fff',
    },
}));

const KEY_LABELS = {
    A: 'Works with Assistance',
    I: 'Works Independently',
    P: 'Presented',
    N: 'Presented',
};

export default function ReportOfActivities() {
    const classes = useStyles();
    const [activityList, setActivityList] = useState([]);
    const [addClassList, setAddClassList] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [classId, setClassId] = useState("");
    const [sectionId, setSectionId] = useState("");
    const [sectionList, setSectionList] = useState([]);
    const [studentId, setStudentId] = useState("");
    const [keyValue, setKeyValue] = useState("");
    const [noteOpen, setNoteOpen] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [pg, setPg] = useState(0);
    const [rpg, setRpg] = useState(100);

    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};

    useEffect(() => {
        getAddClassList();
        return () => {
            setActivityList([]);
            setAddClassList([]);
            setStudentList([]);
        };
    }, []);

    const activeFilterCount =
        (classId ? 1 : 0) +
        (sectionId ? 1 : 0) +
        (studentId ? 1 : 0) +
        (keyValue ? 1 : 0);

    const handleResetFilters = () => {
        setStudentId("");
        setKeyValue("");
        setSearchTerm("");
        setPg(0);
        if (userDetails.role === "TEACHER" && userDetails.classId) {
            setClassId(userDetails.classId);
            setSectionId(userDetails.sectionId || "");
            getSectionListByClass(userDetails.classId);
            getStudentList(userDetails.classId, userDetails.sectionId || undefined);
        } else if (addClassList.length > 0) {
            const firstClassId = addClassList[0]._id;
            setClassId(firstClassId);
            setSectionId("");
            getSectionListByClass(firstClassId);
            getStudentList(firstClassId);
        }
    };

    const getSectionListByClass = (selectedClassId) => {
        if (!selectedClassId) {
            setSectionList([]);
            setSectionId("");
            return;
        }
        SectionService.findSectionList({
            schooleId: userDetails.schooleId,
            classId: selectedClassId,
        })
            .then((res) => {
                setSectionList(Array.isArray(res) ? res : []);
            })
            .catch(() => setSectionList([]));
    };

    const getAddClassList = () => {
        AddClassService.getAllAddClass(userDetails.schooleId).then((res) => {
            const list = Array.isArray(res) ? res : [];
            setAddClassList(list);
            if (userDetails.role === "TEACHER" && userDetails.classId) {
                setClassId(userDetails.classId);
                setSectionId(userDetails.sectionId || "");
                getSectionListByClass(userDetails.classId);
                getStudentList(userDetails.classId, userDetails.sectionId || undefined);
            } else if (list.length > 0) {
                const firstClassId = list[0]._id;
                setClassId(firstClassId);
                setSectionId("");
                getSectionListByClass(firstClassId);
                getStudentList(firstClassId);
            } else {
                setClassId("");
            }
        }).catch(() => {});
    };

    const findActivityList = () => {
        const mappedKey = keyValue === "N" ? "P" : keyValue;
        const keys = {
            classId: classId,
            schooleId: userDetails.schooleId,
            studentId: studentId,
            ...(sectionId && { sectionId }),
            ...(mappedKey && { key: mappedKey }),
        };

        ActivityService.findActivityList(keys).then((res) => {
            setActivityList(Array.isArray(res) ? res : []);
            setPg(0);
        }).catch((err) => {
            console.error("API Error:", err);
        });
    };

    const getStudentList = (selectedClassId, overrideSectionId) => {
        if (!selectedClassId) return;
        const body = { classId: selectedClassId };
        const effectiveSectionId = overrideSectionId !== undefined ? overrideSectionId : (sectionId || userDetails.sectionId);
        if (effectiveSectionId) body.sectionId = effectiveSectionId;
        StudentService.getAllStudentById(userDetails.schooleId, body).then((res) => {
            const studentDetails = res.map((s) => ({
                _id: s._id,
                studentName: `${s.firstName} ${s.lastName}`,
                status: true,
            }));
            setStudentList(studentDetails);
        }).catch(() => {});
    };

    const filteredActivityList = activityList.filter(
        (activitydetails) => activitydetails.activityRecordsId && activitydetails.activityRecordsId.length > 0
    );

    const flatRows = filteredActivityList.flatMap((activitydetails) =>
        activitydetails.activityRecordsId.map((record) => ({
            record,
            activitydetails,
            studentName: activitydetails.studentId
                ? `${activitydetails.studentId.firstName || ''} ${activitydetails.studentId.lastName || ''}`.trim()
                : '',
            className: activitydetails.classId ? activitydetails.classId.className : '',
            sectionName: activitydetails.sectionId ? activitydetails.sectionId.sectionName : '-',
        }))
    );

    const searchedRows = flatRows.filter((row) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        const r = row.record;
        return (
            row.studentName.toLowerCase().includes(term) ||
            row.className.toLowerCase().includes(term) ||
            (r.superActivityId?.superActivityName || '').toLowerCase().includes(term) ||
            (r.activityId?.activityName || '').toLowerCase().includes(term) ||
            (r.subActivityId?.subActivityName || '').toLowerCase().includes(term) ||
            (r.remarks || '').toLowerCase().includes(term)
        );
    });

    const studentInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        return name.slice(0, 2).toUpperCase();
    };

    return (
        <Box p={{ xs: 1, sm: 2 }}>
            <Box className={classes.pageHeader}>
                <Box>
                    <Typography className={classes.pageTitle}>Activity Reports</Typography>
                    <Typography className={classes.pageSubtitle}>
                        Search student activity records by class, section, student, and status key.
                    </Typography>
                </Box>
            </Box>

            {noteOpen && (
                <Box className={classes.noteBanner}>
                    <Box className={classes.noteIconWrap}>
                        <InfoOutlinedIcon style={{ fontSize: 18 }} />
                    </Box>
                    <Box flex={1}>
                        <Typography style={{ fontWeight: 600, color: '#1f3a5f', fontSize: '0.9rem' }}>
                            Activity status report
                        </Typography>
                        <Typography style={{ color: '#46566b', fontSize: '0.85rem', marginTop: 2 }}>
                            Filter by class, section, student, and key (Presented, Works with Assistance, Works Independently), then click Search.
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setNoteOpen(false)}>
                        <CloseIcon style={{ fontSize: 18, color: '#7a8aa0' }} />
                    </IconButton>
                </Box>
            )}

            <Card elevation={0} className={classes.filtersCard}>
                <Box className={classes.filtersTopRow}>
                    <span className={classes.filtersChip} onClick={() => setFiltersOpen((v) => !v)}>
                        <FilterListIcon style={{ fontSize: 18 }} />
                        Filters
                        {activeFilterCount > 0 && <span className={classes.countBadge}>{activeFilterCount}</span>}
                        {filtersOpen ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />}
                    </span>
                </Box>

                <Collapse in={filtersOpen}>
                    <Grid container spacing={2} style={{ marginTop: 8 }}>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <Typography className={classes.fieldLabel}>
                                <SchoolIcon style={{ fontSize: 16 }} /> Class
                            </Typography>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <Select
                                    displayEmpty
                                    name="classId"
                                    value={classId}
                                    onChange={(e) => {
                                        const selected = e.target.value;
                                        setClassId(selected);
                                        setSectionId("");
                                        getSectionListByClass(selected);
                                        getStudentList(selected, undefined);
                                    }}
                                >
                                    <MenuItem value=""><em>Select Class</em></MenuItem>
                                    {addClassList.map(({ _id, className }) => (
                                        <MenuItem key={_id} value={_id}>{className}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <Typography className={classes.fieldLabel}>
                                <SchoolIcon style={{ fontSize: 16 }} /> Section (Optional)
                            </Typography>
                            <FormControl variant="outlined" size="small" fullWidth disabled={!classId || sectionList.length === 0}>
                                <Select
                                    displayEmpty
                                    value={sectionId}
                                    onChange={(e) => {
                                        const newSectionId = e.target.value;
                                        setSectionId(newSectionId);
                                        if (classId) getStudentList(classId, newSectionId || undefined);
                                    }}
                                >
                                    <MenuItem value=""><em>All Sections</em></MenuItem>
                                    {sectionList.map(({ _id, sectionName }) => (
                                        <MenuItem key={_id} value={_id}>{sectionName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <Typography className={classes.fieldLabel}>
                                <PersonIcon style={{ fontSize: 16 }} /> Student
                            </Typography>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <Select
                                    displayEmpty
                                    name="studentId"
                                    value={studentId}
                                    onChange={(e) => setStudentId(e.target.value)}
                                >
                                    <MenuItem value=""><em>All Students</em></MenuItem>
                                    {studentList.map(({ _id, studentName }, index) => (
                                        <MenuItem key={index} value={_id}>{studentName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Typography className={classes.fieldLabel}>
                                <VpnKeyIcon style={{ fontSize: 16 }} /> Key
                            </Typography>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <Select
                                    displayEmpty
                                    name="key"
                                    value={keyValue}
                                    onChange={(e) => setKeyValue(e.target.value)}
                                >
                                    <MenuItem value=""><em>All Keys</em></MenuItem>
                                    <MenuItem value="A">Works with Assistance</MenuItem>
                                    <MenuItem value="I">Works Independently</MenuItem>
                                    <MenuItem value="N">Presented</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <Box display="flex" alignItems="flex-end" height="100%" pb={0.5}>
                                <Button variant="contained" className={classes.primaryBtn} onClick={findActivityList}>
                                    Search
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button className={classes.resetBtn} variant="outlined" onClick={handleResetFilters}>
                            Reset
                        </Button>
                    </Box>
                </Collapse>
            </Card>

            <Card elevation={0} className={classes.tableCard}>
                <Box className={classes.toolbar}>
                    <Typography style={{ fontWeight: 700, color: '#0f2d4d' }}>
                        {searchedRows.length} Record{searchedRows.length !== 1 ? 's' : ''}
                    </Typography>
                    <Box display="flex" alignItems="center" gridGap={10}>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search records..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPg(0);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ fontSize: 18, color: '#9aa6b6' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Tooltip title="Refresh">
                            <IconButton className={classes.iconBtnOutlined} size="small" onClick={findActivityList}>
                                <RefreshIcon style={{ fontSize: 20, color: '#46566b' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <TableContainer component={Paper} elevation={0} style={{ maxHeight: 520 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>Student</TableCell>
                                <TableCell className={classes.headCell}>Class</TableCell>
                                <TableCell className={classes.headCell}>Section</TableCell>
                                <TableCell className={classes.headCell}>Area Of Work</TableCell>
                                <TableCell className={classes.headCell}>Activity</TableCell>
                                <TableCell className={classes.headCell}>Exercise</TableCell>
                                <TableCell className={classes.headCell}>Remarks</TableCell>
                                <TableCell className={classes.headCell}>Date</TableCell>
                                <TableCell className={classes.headCell}>Key</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {searchedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" style={{ padding: 32, color: '#7a8aa0' }}>
                                        No records found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                searchedRows.slice(pg * rpg, pg * rpg + rpg).map(({ record, studentName, className, sectionName }) => (
                                    <TableRow key={record._id} hover>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gridGap={8}>
                                                <Avatar className={classes.studentAvatar}>{studentInitials(studentName)}</Avatar>
                                                <Typography style={{ fontWeight: 600, color: '#0f2d4d', fontSize: '0.875rem' }}>
                                                    {studentName || '—'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={className || '—'} className={classes.classChip} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={sectionName || '—'} className={classes.sectionChip} />
                                        </TableCell>
                                        <TableCell>{record.superActivityId ? record.superActivityId.superActivityName : 'N/A'}</TableCell>
                                        <TableCell>{record.activityId ? record.activityId.activityName : 'N/A'}</TableCell>
                                        <TableCell>{record.subActivityId ? record.subActivityId.subActivityName : 'N/A'}</TableCell>
                                        <TableCell>{record.remarks || '—'}</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gridGap={6} style={{ color: '#46566b' }}>
                                                <EventIcon style={{ fontSize: 16, color: '#9aa6b6' }} />
                                                {record.date || '—'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={KEY_LABELS[record.key] || record.key || '—'}
                                                className={classes.keyChip}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
}
